import { SubscriptionType } from "@components/_subscription/helpers/constants"
import { Button } from "@components/ui/button"
import { Heading } from "@components/ui/typograhpy/Heading"
import { Text } from "@components/ui/typograhpy/Text"
import { ActivePlanType } from "@lib/get-active-plan-type"
import { useGetSubscriptionDetails } from "@lib/hooks/use-get-subscription-details"
import Link from "next/link"
import React, { FC } from "react"
import { PlanCardUsp } from "../PlanCardUsp"
import { CancelSubscriptionModal } from "./CancelSubscriptionModal"
import {
  usePrimaryButtonVariant,
  UsePrimaryButtonVariantPayload,
} from "./hooks/use-primary-button-variant"

type FreePlanCardPropsVariantStatic = {
  variant: "static"
  isActivePlan?: never
  isCancelled?: never
  periodEnd?: never
}
type FreePlanCardPropsVariantDynamic = {
  variant: "dynamic"
  isActivePlan?: boolean
  isCancelled?: boolean
  periodEnd?: string
}

export type FreePlanCardProps = (
  | FreePlanCardPropsVariantStatic
  | FreePlanCardPropsVariantDynamic
) & {
  activePlanType?: ActivePlanType
}

export const FreePlanCard: FC<FreePlanCardProps> = (props) => {
  const primaryButtonVariant = usePrimaryButtonVariant(props)
  const primaryButton = getPrimaryButton(
    primaryButtonVariant,
    props.periodEnd,
    props.activePlanType,
  )
  const { data: subscriptionDetails } = useGetSubscriptionDetails()
  const subscriptionDetail = subscriptionDetails[SubscriptionType.FREE]

  return (
    <div className="bg-border-disabled relative rounded-md px-5 py-6 md:px-6">
      <div className="mb-6">
        <Heading variant="h6" as="h2" className="">
          Try-out
        </Heading>
        <Text variant="regular" className="text-shade-400">
          {subscriptionDetail.initialCredit} credits
        </Text>
      </div>
      <div className="mb-6">
        <Heading variant="h4" as="h3">
          Free
        </Heading>
        <Text variant="regular" className="font-semibold">
          For a lifetime
        </Text>
      </div>
      <div className="mb-8">{primaryButton}</div>
      <ul>
        {subscriptionDetail.usps.map((usp, index) => (
          <PlanCardUsp variant="default" key={index}>
            {usp}
          </PlanCardUsp>
        ))}
      </ul>
    </div>
  )
}

function getPrimaryButton(
  variant: UsePrimaryButtonVariantPayload,
  periodEnd?: string,
  activePlanType?: ActivePlanType,
): React.ReactNode {
  switch (variant) {
    case "is-active-plan": {
      return (
        <Button
          variant="white"
          width="fill"
          status="disabled"
          className="text-shade-800"
          leftIcon={activePlanType === "FREE" ? "check" : undefined}
        >
          Current plan
        </Button>
      )
    }
    case "pending-downgrade": {
      return (
        <Button
          leftIcon={"check"}
          variant="secondary"
          width="fill"
          status="disabled"
        >
          Pending downgrade
        </Button>
      )
    }
    case "show-downgrade-modal": {
      return (
        <CancelSubscriptionModal
          periodEnd={periodEnd as string}
          activePlanType={activePlanType}
          status="idle"
        />
      )
    }
    case "disable-downgrade-modal": {
      return (
        <CancelSubscriptionModal
          periodEnd={periodEnd as string}
          activePlanType={activePlanType}
          status="disabled"
        />
      )
    }
    default:
      return (
        <Link href="/signup/create-account" passHref legacyBehavior>
          <Button variant="secondary" width="fill" as="link">
            Get started for free
          </Button>
        </Link>
      )
  }
}
